import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../../Sidebar/Sidebar";
import Header from "../../../Header/Header";
import Cookies from "js-cookie";
import { FiEdit, FiTrash, FiPlus, FiX, FiCalendar, FiClock } from "react-icons/fi";
import { MdRemoveRedEye, MdEvent, MdEventAvailable } from "react-icons/md";
import { HiOutlineSearch, HiOutlineCalendar, HiOutlineClock } from "react-icons/hi";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-hot-toast";
import "react-loading-skeleton/dist/skeleton.css";

const EventSection = () => {
  // Sidebar state and toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ALL ORIGINAL STATE
  const [newEventData, setNewEventData] = useState({
    title: "",
    description: "",
    eventDate: "",
    image: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const itemsPerPage = 10;

  const authToken = Cookies.get("authToken");
  const adminId = localStorage.getItem("adminId");

  // Calculate event statistics
  const stats = useMemo(() => {
    const total = events.length;
    const upcoming = events.filter(e => e.eventStatus === "Upcoming").length;
    const started = events.filter(e => e.eventStatus === "Started").length;
    return { total, upcoming, started };
  }, [events]);

  // Filter and search events
  useEffect(() => {
    let filtered = events;

    // Apply search filter
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(event =>
        event.eventStatus?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredEvents(filtered);
    setCurrentPage(1);
  }, [searchTerm, events, statusFilter]);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllEvent`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        console.log("Fetch response:", result);

        // Check different possible response structures
        let eventsData = [];
        if (result.data?.events) {
          eventsData = result.data.events;
        } else if (result.message?.events) {
          eventsData = result.message.events;
        } else if (Array.isArray(result.data)) {
          eventsData = result.data;
        } else if (Array.isArray(result.message)) {
          eventsData = result.message;
        }

        setEvents(eventsData);
        setFilteredEvents(eventsData);
        setError(null);

        if (eventsData.length > 0) {
          toast.success(`Loaded ${eventsData.length} events`, {
            icon: '📅',
            duration: 2000
          });
        }
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Failed to load events: " + err.message);
        setEvents([]);
        setFilteredEvents([]);
        toast.error("Failed to load events");
      } finally {
        setLoading(false);
      }
    };

    if (authToken) {
      fetchEvents();
    }
  }, [authToken]);

  // ALL ORIGINAL FUNCTIONS
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEventData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setNewEventData({
      title: "",
      description: "",
      eventDate: "",
      image: "",
    });
    setIsEditing(false);
    setEditingEventId(null);
    setError(null);
  };

  const openEventDetails = (event) => setSelectedEvent(event);
  const closeEventDetails = () => setSelectedEvent(null);

  const handleSaveEvent = async () => {
    if (
      !newEventData.title ||
      !newEventData.description ||
      !newEventData.eventDate
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    try {
      if (isEditing) {
        setIsUpdating(true);
      } else {
        setIsCreating(true);
      }

      const formData = new FormData();
      formData.append("title", newEventData.title);
      formData.append("description", newEventData.description);
      formData.append("eventDate", newEventData.eventDate);
      formData.append("adminProfile", adminId);

      if (newEventData.image instanceof File) {
        formData.append("image", newEventData.image);
      } else if (typeof newEventData.image === "string" && isEditing) {
        formData.append("image", newEventData.image);
      }

      const url = isEditing
        ? `${process.env.REACT_APP_BACKEND_URL}/apis/admin/updateUpComingEvent/${editingEventId}`
        : `${process.env.REACT_APP_BACKEND_URL}/apis/admin/createUpComingEvent`;

      const response = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        if (!isEditing) {
          toast.success("Event added successfully!");
        } else {
          toast.success("Event updated successfully!");
        }

        // Refresh events
        try {
          const refreshResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllEvent`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          );

          const refreshData = await refreshResponse.json();

          let eventsData = [];
          if (refreshData.data?.events) {
            eventsData = refreshData.data.events;
          } else if (refreshData.message?.events) {
            eventsData = refreshData.message.events;
          } else if (Array.isArray(refreshData.data)) {
            eventsData = refreshData.data;
          } else if (Array.isArray(refreshData.message)) {
            eventsData = refreshData.message;
          }

          setEvents(eventsData);
          setFilteredEvents(eventsData);
        } catch (refreshErr) {
          console.error("Error refreshing events:", refreshErr);
          if (isEditing) {
            setEvents(prev => prev.map(event =>
              event._id === editingEventId
                ? { ...event, ...newEventData, image: newEventData.image || event.image }
                : event
            ));
          }
        }

        handleModalClose();
      } else {
        setError(data.message || "Failed to save event");
      }
    } catch (err) {
      console.error("Save error:", err);
      setError("Failed to save event: " + err.message);
    } finally {
      setIsCreating(false);
      setIsUpdating(false);
    }
  };

  const openDeleteConfirm = (event) => {
    setEventToDelete(event);
    setDeleteConfirmModal(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmModal(false);
    setEventToDelete(null);
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    try {
      setIsDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/deleteUpComingEvent/${eventToDelete._id}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const data = await response.json();
      if (data.success) {
        toast.success("Event deleted successfully!");
        setEvents((prev) =>
          prev.filter((event) => event._id !== eventToDelete._id)
        );
        setFilteredEvents((prev) =>
          prev.filter((event) => event._id !== eventToDelete._id)
        );
        closeDeleteConfirm();
      } else {
        throw new Error(data.message || "Failed to delete event");
      }
    } catch (err) {
      setError("Failed to delete event: " + err.message);
      toast.error("Failed to delete event");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditEvent = (event) => {
    setNewEventData({
      title: event.title,
      description: event.description,
      eventDate: event.eventDate.split("T")[0],
      image: event.image,
    });
    setEditingEventId(event._id);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getCountdownText = (eventDate) => {
    const now = new Date();
    const event = new Date(eventDate);
    const diffTime = event - now;

    if (diffTime < 0) return "Started";

    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

    if (diffDays > 30) {
      return `In ${Math.ceil(diffDays / 30)} months`;
    } else if (diffDays > 1) {
      return `In ${diffDays} days`;
    } else if (diffHours > 1) {
      return `In ${diffHours} hours`;
    } else {
      return "Today";
    }
  };

  const getStatusBadge = (status, eventDate) => {
    if (status === "Started") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700 border border-gray-300">
          <FiClock className="w-3 h-3 mr-1" />
          Started
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          <FiCalendar className="w-3 h-3 mr-1" />
          {getCountdownText(eventDate)}
        </span>
      );
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </motion.div>
  );

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredEvents.slice(start, start + itemsPerPage);
  }, [filteredEvents, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSkeletonCards = () => {
    return Array(3).fill(0).map((_, i) => (
      <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex justify-between">
          <div className="flex-1">
            <Skeleton width="70%" height={20} />
            <Skeleton width="40%" height={16} className="mt-2" />
          </div>
          <Skeleton width={80} height={24} />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Skeleton width={32} height={32} circle />
          <Skeleton width={32} height={32} circle />
          <Skeleton width={32} height={32} circle />
        </div>
      </div>
    ));
  };

  const renderSkeletonRows = () => {
    return Array(5).fill(0).map((_, index) => (
      <tr key={`skeleton-${index}`} className="even:bg-gray-50">
        <td className="px-6 py-4">
          <div className="flex items-center">
            <Skeleton width={40} height={40} className="mr-3" />
            <Skeleton width={150} />
          </div>
        </td>
        <td className="px-6 py-4"><Skeleton width={200} /></td>
        <td className="px-6 py-4"><Skeleton width={120} /></td>
        <td className="px-6 py-4"><Skeleton width={80} /></td>
        <td className="px-6 py-4">
          <div className="flex gap-2">
            <Skeleton width={32} height={32} circle />
            <Skeleton width={32} height={32} circle />
            <Skeleton width={32} height={32} circle />
          </div>
        </td>
      </tr>
    ));
  };

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 px-4"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
        <MdEvent className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {searchTerm || statusFilter !== "all" ? 'No events found' : 'No events yet'}
      </h3>
      <p className="text-gray-500 mb-6">
        {searchTerm || statusFilter !== "all"
          ? 'Try adjusting your search or filters'
          : 'Create your first event to get started'}
      </p>
      {(searchTerm || statusFilter !== "all") && (
        <button
          onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
          }}
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <FiX className="w-4 h-4 mr-2" />
          Clear filters
        </button>
      )}
    </motion.div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/events" />

      <div className="flex-1 w-0">
        <Header toggleSidebar={toggleSidebar} />

        <main className="p-4 lg:p-6 overflow-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200"
          >
            {/* Header with stats */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <MdEvent className="w-8 h-8 text-gray-600" />
                    Event Management
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
                    {searchTerm && ` for "${searchTerm}"`}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700 transition-shadow"
                      aria-label="Search events"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="w-full sm:w-auto bg-gray-800 text-white px-4 py-2.5 rounded-lg hover:bg-gray-900 flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-50 shadow-sm"
                    disabled={loading}
                  >
                    <FiPlus className="w-4 h-4" />
                    Add Event
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              {!loading && events.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <StatCard title="Total Events" value={stats.total} icon={MdEvent} color="gray" />
                  <StatCard title="Upcoming" value={stats.upcoming} icon={HiOutlineCalendar} color="green" />
                  <StatCard title="Started" value={stats.started} icon={HiOutlineClock} color="gray" />
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              <button
                onClick={() => setStatusFilter("all")}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${statusFilter === "all"
                    ? "bg-gray-700 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                All Events
              </button>
              <button
                onClick={() => setStatusFilter("upcoming")}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${statusFilter === "upcoming"
                    ? "bg-gray-700 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setStatusFilter("started")}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${statusFilter === "started"
                    ? "bg-gray-700 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Started
              </button>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm"
              >
                {error}
              </motion.div>
            )}

            {/* Mobile Card View */}
            <div className="block lg:hidden">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {renderSkeletonCards()}
                  </motion.div>
                ) : filteredEvents.length === 0 ? (
                  renderEmptyState()
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {paginatedEvents.map((event, index) => (
                      <motion.div
                        key={event._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-800">{event.title}</h3>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {event.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <HiOutlineCalendar className="w-4 h-4 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {formatDate(event.eventDate)}
                              </span>
                            </div>
                          </div>
                          {getStatusBadge(event.eventStatus, event.eventDate)}
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => openEventDetails(event)}
                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View details"
                          >
                            <MdRemoveRedEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditEvent(event)}
                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit event"
                          >
                            <FiEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm(event)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete event"
                          >
                            <FiTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <AnimatePresence mode="wait">
                    {loading ? (
                      renderSkeletonRows()
                    ) : filteredEvents.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12">
                          {renderEmptyState()}
                        </td>
                      </tr>
                    ) : (
                      paginatedEvents.map((event, index) => (
                        <motion.tr
                          key={event._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              {event.image && (
                                <img
                                  src={event.image}
                                  alt={event.title}
                                  className="w-10 h-10 object-cover rounded-lg mr-3 border border-gray-200"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}
                              <span className="text-sm font-medium text-gray-800">
                                {event.title}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600 line-clamp-2 max-w-xs">
                              {event.description}
                            </p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500">
                              <HiOutlineCalendar className="w-4 h-4 mr-2 text-gray-400" />
                              {formatDate(event.eventDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(event.eventStatus, event.eventDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEventDetails(event)}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                title="View details"
                              >
                                <MdRemoveRedEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditEvent(event)}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit event"
                              >
                                <FiEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openDeleteConfirm(event)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete event"
                              >
                                <FiTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredEvents.length > itemsPerPage && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredEvents.length)} of{' '}
                  {filteredEvents.length} events
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <FaChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                              ? 'bg-gray-700 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <FaChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </main>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={handleModalClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {isEditing ? "Edit Event" : "Create New Event"}
                  </h3>
                  <button
                    onClick={handleModalClose}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiX size={20} />
                  </button>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-200"
                  >
                    {error}
                  </motion.div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newEventData.title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700"
                      placeholder="Enter event title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={newEventData.description}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700"
                      rows="4"
                      placeholder="Enter event description"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date & Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      name="eventDate"
                      value={newEventData.eventDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      name="image"
                      onChange={(e) =>
                        setNewEventData((prev) => ({
                          ...prev,
                          image: e.target.files[0],
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700"
                    />
                    {isEditing && newEventData.image && typeof newEventData.image === "string" && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-2">Current Image:</p>
                        <div className="relative inline-block">
                          <img
                            src={newEventData.image}
                            alt="Current Event"
                            className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={handleModalClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEvent}
                    disabled={isCreating || isUpdating}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {(isCreating || isUpdating) && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {isUpdating ? "Updating..." : isCreating ? "Creating..." : isEditing ? "Update Event" : "Create Event"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Event Details Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeEventDetails}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800">Event Details</h3>
                  <button
                    onClick={closeEventDetails}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiX size={24} />
                  </button>
                </div>

                {selectedEvent.image && (
                  <div className="mb-6">
                    <img
                      src={selectedEvent.image}
                      alt={selectedEvent.title}
                      className="w-full h-64 object-cover rounded-lg border border-gray-200"
                    />
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-800 mb-2">{selectedEvent.title}</h4>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center text-gray-500">
                        <HiOutlineCalendar className="w-4 h-4 mr-1" />
                        {formatDate(selectedEvent.eventDate)}
                      </span>
                      {getStatusBadge(selectedEvent.eventStatus, selectedEvent.eventDate)}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h5 className="text-lg font-semibold text-gray-700 mb-2">Description</h5>
                    <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                      {selectedEvent.description}
                    </p>
                  </div>

                  {selectedEvent.countdown && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Countdown</h5>
                      <div className="flex gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-800">{selectedEvent.countdown.days}</p>
                          <p className="text-xs text-gray-500">Days</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-800">{selectedEvent.countdown.hours}</p>
                          <p className="text-xs text-gray-500">Hours</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-800">{selectedEvent.countdown.minutes}</p>
                          <p className="text-xs text-gray-500">Minutes</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeEventDetails}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeDeleteConfirm}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Delete Event</h3>
                  <button
                    onClick={closeDeleteConfirm}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiX size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-600">
                    Are you sure you want to delete the event{' '}
                    <span className="font-semibold text-gray-800">"{eventToDelete?.title}"</span>?
                  </p>
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <FiClock className="w-4 h-4" />
                    This action cannot be undone.
                  </p>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={closeDeleteConfirm}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteEvent}
                    disabled={isDeleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {isDeleting && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {isDeleting ? "Deleting..." : "Delete Event"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventSection;